import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { statsApi } from '../api/api';

const NAV_ITEMS = [
  { to: '/admin/dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', exact: true },
  { to: '/admin/home', icon: 'fas fa-home', label: 'Home' },
  { to: '/admin/events', icon: 'fas fa-calendar-alt', label: 'Events & Kathas' },
  { to: '/admin/gallery', icon: 'fas fa-images', label: 'Gallery' },
  { to: '/admin/katha', icon: 'fas fa-video', label: 'Katha Videos' },
  { to: '/admin/contacts', icon: 'fas fa-envelope', label: 'Contact Inbox', badgeKey: 'newContacts' },
  { to: '/admin/social', icon: 'fas fa-share-alt', label: 'Social Media' },
  { to: '/admin/ashram', icon: 'fas fa-place-of-worship', label: 'Ashram Details' },
  { to: '/admin/seva', icon: 'fas fa-hand-holding-heart', label: 'Seva Donation' },
];

const PAGE_TITLES = {
  '/admin/dashboard': 'Dashboard',
  '/admin/home': 'Home',
  '/admin/events': 'Events & Kathas',
  '/admin/gallery': 'Gallery',
  '/admin/katha': 'Katha Videos',
  '/admin/contacts': 'Contact Inbox',
  '/admin/social': 'Social Media',
  '/admin/ashram': 'Ashram Details',
  '/admin/seva': 'Seva Donation',
};

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    statsApi.get()
      .then((res) => {
        if (res.success) setBadges({ newContacts: res.data.newContacts || 0 });
      })
      .catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const pageTitle = PAGE_TITLES[location.pathname] || 'Admin';

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Brand with logo */}
      <div className="px-5 py-5 border-b border-orange-100">
        <Link to="/admin/dashboard" className="flex items-center gap-3 group" onClick={() => setSidebarOpen(false)}>
          <img
            src="/logo.jpg"
            alt="Logo"
            className="w-11 h-11 rounded-full object-cover shadow-md border-2 border-orange-300 group-hover:scale-105 transition-transform flex-shrink-0"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="min-w-0">
            <div className="font-bold text-gray-800 text-sm leading-tight">Shree Ram Ranuja Dham</div>
            <div className="text-xs font-semibold admin-link-orange">Admin Panel</div>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <p className="px-5 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Main Menu</p>
        {NAV_ITEMS.map((item) => {
          const badge = item.badgeKey ? badges[item.badgeKey] : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={!!item.exact}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 mx-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 mb-0.5 ${
                  isActive ? 'text-white admin-nav-active' : 'text-gray-600 hover:bg-orange-50'
                }`
              }
            >
              <i className={`${item.icon} w-4 text-center`}></i>
              <span className="flex-1">{item.label}</span>
              {badge > 0 && (
                <span className="min-w-5 h-5 px-1.5 rounded-full admin-badge-red text-xs font-bold flex items-center justify-center">
                  {badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t border-orange-100">
        <div className="flex items-center gap-3 mb-3 px-2">
          <div className="w-9 h-9 rounded-full admin-avatar flex items-center justify-center flex-shrink-0 font-bold text-sm shadow">
            {(admin?.username || 'A').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{admin?.username}</div>
            <div className="text-xs text-green-600 font-medium flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
              Online
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 text-sm admin-btn-signout py-2.5 px-3 rounded-xl transition-colors font-medium"
        >
          <i className="fas fa-sign-out-alt"></i>
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-poppins">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-orange-100 shadow-sm flex-shrink-0">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setSidebarOpen(false)}></div>
          <aside className="relative z-50 w-72 flex flex-col bg-white shadow-2xl">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 z-10"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-orange-100 px-4 md:px-6 py-3 flex items-center gap-3 shadow-sm flex-shrink-0">
          <button
            className="md:hidden text-gray-500 hover:text-orange-600 p-2 rounded-lg hover:bg-orange-50"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="fas fa-bars text-lg"></i>
          </button>

          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-400 hidden sm:block mb-0.5">
              Admin / <span className="admin-link-orange font-medium">{pageTitle}</span>
            </div>
            <h1 className="text-base md:text-lg font-bold text-gray-800 truncate">{pageTitle}</h1>
          </div>

          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex admin-btn-primary px-4 py-2 text-sm"
          >
            <i className="fas fa-external-link-alt text-xs"></i>
            View Site
          </a>
        </header>

        <main className="flex-1 overflow-y-auto px-3 py-3 md:px-4 md:py-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
